import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { devError } from '../lib/logger';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Download, ShieldAlert, RefreshCw, Loader2, Database, HardDrive, CheckCircle2, XCircle, Trash2, FileText, Image as ImageIcon, AlertTriangle, RotateCw, CloudUpload } from 'lucide-react';

const fmtBytes = (n = 0) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Hoisted: small leaf components kept at module scope so they aren't re-created
// on every parent render (which would force React to remount the subtree).
function StatusPill({ ok }) {
  return ok
    ? <Badge className="bg-[#E8F5E9] text-[#2D6A4F] text-[10px]"><CheckCircle2 size={10} className="mr-1" />OK</Badge>
    : <Badge className="bg-[#FFEBEE] text-[#9E2A2B] text-[10px]"><XCircle size={10} className="mr-1" />DOWN</Badge>;
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-3 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-1">
        {Icon && <Icon size={11} />}{label}
      </p>
      <p className="text-lg font-medium text-[#1A1A1A]">{value}</p>
    </div>
  );
}

// Color classes for PDF job / mirror-job status badges (replaces nested ternaries)
const JOB_STATUS_COLOR = { completed: 'text-[#2D6A4F]', complete: 'text-[#2D6A4F]', failed: 'text-[#9E2A2B]', error: 'text-[#9E2A2B]' };
const jobStatusColor = (status) => JOB_STATUS_COLOR[status] || 'text-[#C5A059]';

export default function SettingsPage() {
  const { user } = useAuth();
  const isMaster = user?.role === 'super_admin';
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [mirrorBusy, setMirrorBusy] = useState(false);
  const [mirrorStatus, setMirrorStatus] = useState(null);
  const [cleanBusy, setCleanBusy] = useState(false);
  const [clearAllBusy, setClearAllBusy] = useState(false);
  const [pdfMetrics, setPdfMetrics] = useState(null);

  const refreshHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get('/health', { timeout: 15000 });
      setHealth(data);
    } catch (err) {
      devError('Health check failed', err);
      setHealth({ status: 'unreachable', error: err.message });
    }
    setHealthLoading(false);
  }, []);

  const refreshStorage = useCallback(async () => {
    if (!isMaster) return;
    setStorageLoading(true);
    try {
      const { data } = await api.get('/admin/storage', { timeout: 60000 });
      setStorage(data);
    } catch (err) {
      devError('Storage report failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to load storage report');
    }
    setStorageLoading(false);
  }, [isMaster]);

  const refreshPdfMetrics = useCallback(async () => {
    if (!isMaster) return;
    try {
      const { data } = await api.get('/admin/pdf-metrics', { timeout: 15000 });
      setPdfMetrics(data);
    } catch (err) { devError('PDF metrics fetch failed', err); }
  }, [isMaster]);

  useEffect(() => { refreshHealth(); refreshStorage(); refreshPdfMetrics(); }, [refreshHealth, refreshStorage, refreshPdfMetrics]);

  const downloadBackup = async () => {
    setBackupBusy(true);
    const toastId = toast.loading('Preparing backup… this may take 30–60 seconds');
    try {
      const response = await api.get('/admin/backup', { responseType: 'blob', timeout: 300000 });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AARYA24kt_Backup_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Backup downloaded', { id: toastId });
    } catch (err) {
      devError('Backup download failed', err);
      const msg = err.response?.status === 403 ? 'Master Admin access required' :
                  err.code === 'ECONNABORTED' ? 'Backup timed out — try again or check server logs' :
                  formatApiError(err.response?.data?.detail) || 'Backup failed';
      toast.error(msg, { id: toastId });
    }
    setBackupBusy(false);
  };

  // ---- Image mirror job: copies external WP-hosted images into Object Storage
  // so production (which cannot egress to aarya24kt.com) can render PDFs cleanly.
  // Runs as a background task on the backend; we just poll for live progress.
  const pollMirrorStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/mirror-external-images/status');
      setMirrorStatus(data);
      if (data.status === 'running') {
        setTimeout(pollMirrorStatus, 2000);
      } else {
        setMirrorBusy(false);
        if (data.status === 'complete') {
          toast.success(`Mirror complete — ${data.urls_migrated} images copied, ${data.products_migrated} products updated${data.failed_count ? `, ${data.failed_count} failed` : ''}`);
        } else if (data.status === 'error') {
          toast.error(`Mirror job error: ${data.error || 'unknown'}`);
        }
      }
    } catch (err) { devError('Mirror status poll failed', err); }
  }, []);

  const startMirrorJob = async (dryRun) => {
    setMirrorBusy(true);
    try {
      await api.post(`/admin/mirror-external-images?dry_run=${dryRun}`);
      toast.success(dryRun ? 'Dry-run started — scanning…' : 'Mirror started — runs in background');
      setTimeout(pollMirrorStatus, 500);
    } catch (err) {
      setMirrorBusy(false);
      const msg = err.response?.status === 409
        ? 'A mirror job is already running'
        : formatApiError(err.response?.data?.detail) || 'Failed to start mirror job';
      toast.error(msg);
    }
  };

  const cleanCache = async () => {
    if (!window.confirm('Remove all orphaned PDF cache files? Active PDFs and product images are NEVER touched.')) return;
    setCleanBusy(true);
    const toastId = toast.loading('Cleaning orphaned PDF cache…');
    try {
      const { data } = await api.post('/admin/cache/clean', {}, { timeout: 120000 });
      toast.success(`Cleaned ${data.cleaned} file${data.cleaned === 1 ? '' : 's'}, freed ${fmtBytes(data.freed_bytes)}`, { id: toastId });
      refreshStorage();
    } catch (err) {
      devError('Cache clean failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Cache clean failed', { id: toastId });
    }
    setCleanBusy(false);
  };

  const clearAllPdfCache = async () => {
    if (!window.confirm('Delete EVERY cached PDF for EVERY catalog?\n\nUse this when PDFs are showing missing or broken images. The next time someone downloads a catalog PDF it will regenerate from the current live images (takes ~10-30s per catalog). Product images, uploads, brand assets are NEVER touched.')) return;
    setClearAllBusy(true);
    const toastId = toast.loading('Wiping all cached PDFs…');
    try {
      const { data } = await api.post('/admin/cache/clear-all-pdf', {}, { timeout: 180000 });
      toast.success(`Deleted ${data.cleared} cached PDF${data.cleared === 1 ? '' : 's'}, freed ${fmtBytes(data.freed_bytes)}. Catalogs will regenerate on next download.`, { id: toastId });
      refreshStorage();
    } catch (err) {
      devError('Clear-all PDF cache failed', err);
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to wipe PDF cache', { id: toastId });
    }
    setClearAllBusy(false);
  };

  if (!isMaster) {
    return (
      <div className="animate-fade-in" data-testid="settings-restricted">
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
          <ShieldAlert size={40} className="mx-auto text-[#9E2A2B] mb-4" />
          <h2 className="font-serif text-xl mb-2">Master Admin Only</h2>
          <p className="text-sm text-[#5C5C5C]">Settings are restricted to the Master Admin account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Settings</h1>
        <p className="text-sm text-[#5C5C5C] mt-1">System health, storage, backups, and admin tools</p>
      </div>

      {/* Health */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 mb-6" data-testid="settings-health-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg">System Health</h2>
          <Button variant="outline" size="sm" onClick={refreshHealth} disabled={healthLoading} className="rounded-sm border-[#E5E0D8]" data-testid="refresh-health-btn">
            <RefreshCw size={12} className={`mr-1 ${healthLoading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
        {!health ? <p className="text-xs text-[#5C5C5C]">Checking…</p> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]"><div className="flex items-center justify-between mb-1"><span className="flex items-center gap-1.5 text-xs text-[#5C5C5C]"><Database size={12} />Database</span><StatusPill ok={health.db} /></div></div>
            <div className="p-3 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]"><div className="flex items-center justify-between mb-1"><span className="flex items-center gap-1.5 text-xs text-[#5C5C5C]"><HardDrive size={12} />Storage</span><StatusPill ok={health.storage} /></div></div>
            <div className="p-3 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]"><p className="text-xs text-[#5C5C5C] mb-1">Status</p><p className="text-sm font-medium uppercase tracking-wider" style={{ color: health.status === 'ok' ? '#2D6A4F' : '#C5A059' }}>{health.status}</p></div>
            <div className="p-3 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]"><p className="text-xs text-[#5C5C5C] mb-1">Version</p><p className="text-sm font-mono">{health.version || '-'}</p></div>
          </div>
        )}
      </div>

      {/* Storage Monitor */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 mb-6" data-testid="settings-storage-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg">Storage & Performance</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refreshStorage} disabled={storageLoading} className="rounded-sm border-[#E5E0D8]" data-testid="refresh-storage-btn">
              <RefreshCw size={12} className={`mr-1 ${storageLoading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" disabled={cleanBusy || !(storage?.totals?.orphan_pdf_count > 0)} onClick={cleanCache} className="bg-[#9E2A2B] hover:bg-[#832122] text-white rounded-sm disabled:opacity-50" data-testid="clean-cache-btn">
              {cleanBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Trash2 size={12} className="mr-1" />}
              Clean Old PDF Cache {storage?.totals?.orphan_pdf_count > 0 && `(${storage.totals.orphan_pdf_count})`}
            </Button>
            <Button size="sm" disabled={clearAllBusy} onClick={clearAllPdfCache} variant="outline" className="rounded-sm border-[#9E2A2B] text-[#9E2A2B] hover:bg-red-50" data-testid="clear-all-pdf-btn" title="Use when cached PDFs show broken or missing images — wipes every cached PDF so they regenerate from current live images.">
              {clearAllBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RotateCw size={12} className="mr-1" />}
              Regenerate All PDFs
            </Button>
          </div>
        </div>

        {!storage ? <p className="text-xs text-[#5C5C5C]">Loading storage report…</p> : (
          <>
            {/* Top stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat icon={Database} label="Total Products" value={storage.totals.products_active} />
              <Stat icon={FileText} label="Total Catalogs" value={storage.totals.catalogs} />
              <Stat icon={HardDrive} label="Object Storage" value={fmtBytes(storage.totals.object_total_bytes)} />
              <Stat icon={ImageIcon} label="Images" value={fmtBytes(storage.totals.image_bytes)} />
              <Stat icon={FileText} label="PDF Cache" value={fmtBytes(storage.totals.pdf_cache_bytes)} />
              <Stat icon={AlertTriangle} label="Orphan PDFs" value={`${storage.totals.orphan_pdf_count} (${fmtBytes(storage.totals.orphan_pdf_bytes)})`} />
              <Stat icon={Database} label="Total Files in DB" value={storage.totals.object_count} />
              <Stat icon={Database} label="Translation Cache" value={storage.db_counts?.translation_cache || 0} />
            </div>

            {/* By category */}
            <div className="mb-6">
              <h3 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-2">By Category</h3>
              <div className="bg-[#FDFBF7] rounded-sm border border-[#E5E0D8] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F2EB]"><tr><th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Path</th><th className="text-right py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Files</th><th className="text-right py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Size</th></tr></thead>
                  <tbody>
                    {storage.by_category.map(c => (
                      <tr key={c.name} className="border-t border-[#E5E0D8]"><td className="py-2 px-3 font-mono text-xs">{c.name}/</td><td className="py-2 px-3 text-right">{c.count}</td><td className="py-2 px-3 text-right">{fmtBytes(c.size)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Largest PDFs + Images side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <h3 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-2">Largest PDFs</h3>
                {storage.largest_pdfs.length === 0 ? <p className="text-xs text-[#888]">No PDFs cached yet</p> : (
                  <ul className="space-y-1 text-xs">
                    {storage.largest_pdfs.map(p => (
                      <li key={p.path} className="flex justify-between gap-3 p-2 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]">
                        <span className="font-mono truncate flex-1" title={p.path}>{p.path.split('/').pop()}</span>
                        <span className="font-medium">{fmtBytes(p.size)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-2">Largest Images</h3>
                {storage.largest_images.length === 0 ? <p className="text-xs text-[#888]">No images uploaded yet</p> : (
                  <ul className="space-y-1 text-xs">
                    {storage.largest_images.map(p => (
                      <li key={p.path} className="flex justify-between gap-3 p-2 bg-[#FDFBF7] rounded-sm border border-[#E5E0D8]">
                        <span className="font-mono truncate flex-1" title={p.path}>{p.path.split('/').pop()}</span>
                        <span className="font-medium">{fmtBytes(p.size)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-[10px] text-[#888] italic">Clean Old PDF Cache only removes orphaned PDFs whose catalog has been edited or deleted. Active catalog PDFs, product images, cover images and brand assets are NEVER touched.</p>
          </>
        )}
      </div>

      {/* Backup */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="settings-backup-card">
        <h2 className="font-serif text-lg mb-2">Complete Backup</h2>
        <p className="text-sm text-[#5C5C5C] mb-4 max-w-2xl">
          Download a single ZIP archive containing JSON exports of all collections (users, products, catalogs, master data, version history, templates) plus the bundled brand assets and a README explaining the contents. User password hashes are stripped — use the admin reset flow after restore.
        </p>
        <Button onClick={downloadBackup} disabled={backupBusy} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="download-backup-btn">
          {backupBusy ? <><Loader2 size={14} className="mr-2 animate-spin" />Preparing backup…</> : <><Download size={14} className="mr-2" />Download Complete Backup</>}
        </Button>
      </div>

      {/* PDF Generation Metrics — surfaces job concurrency, success/fail counts, latest jobs */}
      {pdfMetrics && (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="settings-pdf-metrics-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg flex items-center gap-2"><FileText size={18} className="text-[#C5A059]"/>PDF Generation Metrics</h2>
            <Button variant="ghost" size="sm" onClick={refreshPdfMetrics} className="rounded-sm" data-testid="refresh-pdf-metrics-btn"><RefreshCw size={14}/></Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Concurrency</p><p className="text-base font-medium">{pdfMetrics.concurrency_limit}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">In-flight</p><p className="text-base font-medium text-[#C5A059]">{pdfMetrics.in_flight_count}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Succeeded</p><p className="text-base font-medium text-[#2D6A4F]">{pdfMetrics.succeeded}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Failed</p><p className={`text-base font-medium ${pdfMetrics.failed ? 'text-[#9E2A2B]' : ''}`}>{pdfMetrics.failed}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Avg duration</p><p className="text-base font-medium">{pdfMetrics.avg_duration_s}s</p></div>
          </div>
          {(pdfMetrics.last_jobs?.length || 0) > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[#5C5C5C] hover:text-[#1A1A1A]">Last {pdfMetrics.last_jobs.length} jobs</summary>
              <div className="mt-2 max-h-56 overflow-y-auto border border-[#E5E0D8] rounded-sm bg-[#FDFBF7]">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#F5F2EB] text-[#5C5C5C] text-[10px] uppercase tracking-wider"><th className="text-left px-2 py-1.5">Catalog ID</th><th className="text-left px-2 py-1.5">Status</th><th className="text-left px-2 py-1.5">Duration</th><th className="text-left px-2 py-1.5">Cache</th><th className="text-left px-2 py-1.5">Finished</th><th className="text-left px-2 py-1.5">Error</th></tr></thead>
                  <tbody>
                    {pdfMetrics.last_jobs.map((j) => (
                      <tr key={`${j.catalog_id}-${j.finished_at}`} className="border-t border-[#E5E0D8]">
                        <td className="px-2 py-1.5 font-mono text-[10px]">{j.catalog_id?.slice(0, 8)}…</td>
                        <td className="px-2 py-1.5"><span className={jobStatusColor(j.status)}>{j.status}</span></td>
                        <td className="px-2 py-1.5">{j.duration_s}s</td>
                        <td className="px-2 py-1.5">{j.cache_status || '-'}</td>
                        <td className="px-2 py-1.5 text-[#5C5C5C]">{j.finished_at ? new Date(j.finished_at).toLocaleTimeString() : '-'}</td>
                        <td className="px-2 py-1.5 text-[#9E2A2B] text-[10px] max-w-xs truncate">{j.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {/* Mirror External Images — fixes "PDF images blank in production" */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="settings-mirror-card">
        <h2 className="font-serif text-lg mb-2 flex items-center gap-2"><CloudUpload size={18} className="text-[#C5A059]"/>Mirror External Images to Object Storage</h2>
        <p className="text-sm text-[#5C5C5C] mb-4 max-w-3xl leading-relaxed">
          If product images hosted on the company WordPress site (<code className="text-[#1A1A1A] bg-[#F5F2EB] px-1 rounded text-xs">aarya24kt.com</code>) show as blank in PDFs on production, run this once <strong>from the preview environment</strong>. It downloads every external image and re-hosts it on Emergent Object Storage so production never needs to reach back to WordPress. Runs in the background; this page polls for live progress every 2&nbsp;seconds. Safe to re-run — images already on storage are skipped automatically.
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          <Button onClick={() => startMirrorJob(true)} disabled={mirrorBusy} variant="outline" className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5]" data-testid="mirror-dry-run-btn">
            <RotateCw size={14} className="mr-2" />Preview Count (dry-run)
          </Button>
          <Button onClick={() => startMirrorJob(false)} disabled={mirrorBusy} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="mirror-start-btn">
            {mirrorBusy && mirrorStatus?.status === 'running' ? <><Loader2 size={14} className="mr-2 animate-spin"/>Mirroring…</> : <><CloudUpload size={14} className="mr-2"/>Start Mirror Job</>}
          </Button>
        </div>
        {mirrorStatus && (
          <div className="border-t border-[#E5E0D8] pt-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm" data-testid="mirror-status-grid">
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Status</p><p className={`font-medium ${jobStatusColor(mirrorStatus.status)}`}>{mirrorStatus.status}{mirrorStatus.dry_run ? ' (dry)' : ''}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Products</p><p className="font-medium">{mirrorStatus.scanned} / {mirrorStatus.total_products}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Migrated</p><p className="font-medium">{mirrorStatus.products_migrated}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Images Copied</p><p className="font-medium text-[#2D6A4F]">{mirrorStatus.urls_migrated}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Failed</p><p className={`font-medium ${mirrorStatus.failed_count ? 'text-[#9E2A2B]' : ''}`}>{mirrorStatus.failed_count || 0}</p></div>
          </div>
        )}
        {mirrorStatus?.failed?.length > 0 && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-[#9E2A2B]">Show first {mirrorStatus.failed.length} failed URLs</summary>
            <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto bg-[#FDFBF7] p-2 rounded-sm border border-[#E5E0D8]">
              {mirrorStatus.failed.map((f) => (
                <li key={f.url} className="break-all"><span className="text-[#9E2A2B]">⚠</span> <span className="text-[#1A1A1A]">{f.url}</span> — <span className="text-[#5C5C5C]">{f.error}</span></li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
